
import { useState, useEffect } from 'react';

interface Formation {
  id: number;
  title: string;
  description: string;
  link: string;
  available: boolean;
}

const GRADIENTS = [
  { bg: 'from-blue-50 to-blue-100', border: 'border-blue-200', btn: 'bg-teal-600 hover:bg-teal-700', icon: 'bg-teal-600', iconText: 'ri-line-chart-line' },
  { bg: 'from-orange-50 to-orange-100', border: 'border-orange-200', btn: 'bg-orange-600 hover:bg-orange-700', icon: 'bg-orange-600', iconText: 'ri-shopping-cart-line' },
  { bg: 'from-green-50 to-green-100', border: 'border-green-200', btn: 'bg-green-600 hover:bg-green-700', icon: 'bg-green-600', iconText: 'ri-book-open-line' },
  { bg: 'from-pink-50 to-pink-100', border: 'border-pink-200', btn: 'bg-pink-600 hover:bg-pink-700', icon: 'bg-pink-600', iconText: 'ri-lightbulb-line' },
];

export default function Formation() {
  const [formations, setFormations] = useState<Formation[]>([]);
  const [announcement, setAnnouncement] = useState('');

  useEffect(() => {
    const saved = localStorage.getItem('adminFormations');
    if (saved) {
      setFormations(JSON.parse(saved));
    } else {
      setFormations([
        { id: 1, title: 'Trading', description: 'Apprenez les bases du trading, les stratégies gagnantes et comment analyser les marchés financiers.', link: '', available: true },
        { id: 2, title: 'E-Commerce', description: 'Découvrez comment créer et gérer votre boutique en ligne, attirer des clients et maximiser vos ventes.', link: '', available: true },
      ]);
    }
    const ann = localStorage.getItem('siteAnnouncement') || '';
    setAnnouncement(ann);
  }, []);

  const handleStart = (f: Formation) => {
    if (f.link) {
      window.open(f.link, '_blank');
    } else {
      alert(`La formation "${f.title}" sera bientôt disponible. Restez connecté !`);
    }
  };

  const availableFormations = formations.filter(f => f.available);

  return (
    <div className="space-y-6">
      {announcement && (
        <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 rounded-lg flex items-start gap-3">
          <i className="ri-megaphone-line text-yellow-500 text-xl mt-0.5"></i>
          <div>
            <p className="text-xs font-bold text-yellow-700 mb-1">Annonce de l'administration</p>
            <p className="text-sm text-gray-700">{announcement}</p>
          </div>
        </div>
      )}

      <div className="bg-gradient-to-r from-indigo-600 to-teal-600 rounded-xl p-8 text-white shadow-lg">
        <h2 className="text-2xl font-bold mb-2">Formations disponibles</h2>
        <p className="text-indigo-100">Développez vos compétences et augmentez vos revenus</p>
      </div>

      <div className="bg-white rounded-xl p-6 shadow-lg">
        {availableFormations.length === 0 ? (
          <div className="text-center py-12">
            <i className="ri-book-open-line text-5xl text-gray-200 mb-3"></i>
            <p className="text-gray-500 font-semibold">Aucune formation disponible pour le moment</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {availableFormations.map((f, idx) => {
              const style = GRADIENTS[idx % GRADIENTS.length];
              return (
                <div key={f.id} className={`bg-gradient-to-br ${style.bg} rounded-xl p-6 border-2 ${style.border}`}>
                  <div className="flex items-center space-x-4 mb-4">
                    <div className={`w-16 h-16 ${style.icon} rounded-full flex items-center justify-center`}>
                      <i className={`${style.iconText} text-3xl text-white`}></i>
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-gray-800">{f.title}</h3>
                      <p className="text-sm text-gray-500">Formation complète</p>
                    </div>
                  </div>
                  <p className="text-gray-700 text-sm mb-4">{f.description}</p>
                  <button
                    onClick={() => handleStart(f)}
                    className={`w-full ${style.btn} text-white py-2.5 rounded-lg font-semibold transition whitespace-nowrap cursor-pointer`}
                  >
                    {f.link ? 'Accéder à la formation' : 'Bientôt disponible'}
                  </button>
                </div>
              );
            })}
          </div>
        )}

        <div className="mt-6 bg-gradient-to-r from-green-50 to-green-100 rounded-xl p-6 border-2 border-green-200">
          <h3 className="text-lg font-bold text-gray-800 mb-4 flex items-center">
            <i className="ri-lightbulb-line text-green-600 mr-2"></i>
            Pourquoi suivre nos formations ?
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {['Formations 100% gratuites', 'Contenu pratique et applicable', 'Accès illimité aux ressources', 'Support de la communauté'].map((item, i) => (
              <div key={i} className="flex items-start gap-2">
                <i className="ri-checkbox-circle-fill text-green-500 mt-0.5"></i>
                <span className="text-sm text-gray-700">{item}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="mt-6 bg-teal-50 rounded-lg p-6">
          <h3 className="font-bold text-gray-800 mb-3 flex items-center">
            <i className="ri-telegram-line text-teal-600 mr-2"></i>
            Rejoignez notre communauté
          </h3>
          <p className="text-gray-700 text-sm mb-4">
            Échangez avec d'autres membres, posez vos questions et recevez des conseils d'experts.
          </p>
          <a
            href="https://t.me/+RLed8IfVgMINDg0"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center space-x-2 px-6 py-3 bg-gradient-to-r from-teal-500 to-teal-600 text-white rounded-lg hover:shadow-lg transition whitespace-nowrap cursor-pointer"
          >
            <i className="ri-telegram-line text-xl"></i>
            <span className="font-semibold">Rejoindre le groupe Telegram</span>
          </a>
        </div>
      </div>
    </div>
  );
}
